import { BASE_URL_V3, getUrl } from "../utils/url.ts";
import { fetchFromVolatileServer } from "../utils/fetchFromVolatileServer.ts";
import { PageTypeError } from "@/types/PageType.ts";
import { fetchWithAuth } from "./fetchWithAuth.ts";
import { Post } from "./types/client.ts";
import { CampaignWithId } from "@/utils/campaignUtils.ts";

interface GenericPostParams {
  locale?: string;
  defaultLocale?: string;
  type?: string;
}

interface GetAllPostsOrCustomPostTypesParams extends GenericPostParams {
  currentPage?: number;
  carryOverPosts?: Post[];
  extraParams?: string;
}

interface GetPostForTypeParams extends GenericPostParams {
  slug: string;
  type: string;
  fields?: string;
}

interface GetPostsForIdsParams extends GenericPostParams {
  ids: string;
  type?: string;
}

/**
 * Utility function to fetch and parse JSON responses.
 */
const fetchAndParse = async <T>(url: string): Promise<T | null> => {
  const response = await fetchFromVolatileServer(url);
  if (!response.ok) {
    console.error(`Failed to fetch: ${url}`);
    return null;
  }
  return (await response.json()) as T;
};

/**
 * Generic function to handle paginated fetch for posts.
 */
const fetchPaginatedPosts = async (
  url: string,
  currentPage: number,
  carryOverPosts: Post[],
): Promise<Post[]> => {
  const response = await fetchFromVolatileServer(url);
  const posts = await response.json();

  if ((posts as PageTypeError).data?.status === 404) {
    return [];
  }

  const postsCombined = [...carryOverPosts, ...posts];
  const totalPages = response.headers.get("X-WP-TotalPages");

  if (totalPages && currentPage < parseInt(totalPages)) {
    const nextPageUrl = url.replace(
      `page=${currentPage}`,
      `page=${currentPage + 1}`,
    );
    return await fetchPaginatedPosts(
      nextPageUrl,
      currentPage + 1,
      postsCombined,
    );
  }

  return postsCombined;
};

/**
 * Fetch all posts or custom post types recursively.
 */
export const getAllPostsOrCustomPostTypes = async ({
  type = "posts",
  currentPage = 1,
  carryOverPosts = [],
  locale,
  defaultLocale,
  extraParams,
}: GetAllPostsOrCustomPostTypesParams): Promise<Post[]> => {
  const url = `${getUrl(locale, defaultLocale)}/${type}?per_page=50&page=${currentPage}${
    extraParams || ""
  }`;
  return await fetchPaginatedPosts(url, currentPage, carryOverPosts);
};

/**
 * Fetch post by type and slug.
 */
export const getPostForType = async ({
  slug,
  type,
  locale,
  defaultLocale,
  fields,
}: GetPostForTypeParams): Promise<Post[] | null> => {
  const url = `${getUrl(locale, defaultLocale)}/${type}?slug=${slug}&acf_format=light${
    fields ? `&_fields=${fields}` : ""
  }`;
  return await fetchAndParse(url);
};

/**
 * Fetch posts by IDs.
 */
export const getPostsForIds = async ({
  ids,
  type = "blog",
  locale,
  defaultLocale,
}: GetPostsForIdsParams): Promise<Post[] | null> => {
  const url = `${getUrl(locale, defaultLocale)}/${type}?include=${ids}&acf_format=light`;
  return await fetchAndParse(url);
};

/**
 * Fetch posts for categories.
 */
export const getPostsForCategories = async ({
  ids,
  locale,
  defaultLocale,
}: {
  ids: string;
  locale?: string;
  defaultLocale?: string;
}): Promise<Post[] | null> => {
  const url = `${getUrl(locale, defaultLocale)}/blog?theme_category=${ids}&acf_format=light`;
  return await fetchAndParse(url);
};

/**
 * Fetch a single page by ID.
 */
export const getPageById = async ({
  id,
  locale,
  defaultLocale,
}: {
  id: number;
  locale?: string;
  defaultLocale?: string;
}): Promise<Post | null> => {
  const url = `${getUrl(locale, defaultLocale)}/pages/${id}?acf_format=light`;
  return fetchAndParse(url);
};

/**
 * Fetch all private pages.
 */
export const getAllPrivatePages = async ({
  locale,
  defaultLocale,
}: {
  locale?: string;
  defaultLocale?: string;
}): Promise<Post[]> => {
  return getAllPrivatePostsOrCustomPostTypes("pages", locale, defaultLocale);
};

/**
 * Fetch a private page by slug.
 */
export const getPrivatePage = async (
  slug: string,
  locale?: string,
  defaultLocale?: string,
): Promise<Post[] | null> => {
  return getPrivatePost(slug, "pages", locale, defaultLocale);
};

/**
 * Fetch all private posts or custom post types.
 */
export const getAllPrivatePostsOrCustomPostTypes = async (
  type = "posts",
  locale?: string,
  defaultLocale?: string,
): Promise<Post[]> => {
  return fetchWithAuth(`/${type}`, locale, defaultLocale, {
    status: "private",
    per_page: "100",
    _fields: "slug",
    skip_cache: "1",
  });
};

/**
 * Fetch a private post by slug and type.
 */
export const getPrivatePost = async (
  slug: string,
  type: string,
  locale?: string,
  defaultLocale?: string,
): Promise<Post[] | null> => {
  return fetchWithAuth(`/${type}`, locale, defaultLocale, {
    status: "private",
    slug,
    acf_format: "light",
    skip_cache: "1",
  });
};

/**
 * Fetch campaign posts by category.
 */
export const getCampaignPostsByCategory = async (
  idsArray: string[],
): Promise<CampaignWithId[] | null> => {
  return getCampaignPostsFromUrl(
    `${BASE_URL_V3}/campaigns?categories=${idsArray.join(",")}`,
  );
};

export const getCampaignPostsByCampaigns = async (): Promise<
  CampaignWithId[] | null
> => {
  return getCampaignPostsFromUrl(`${BASE_URL_V3}/campaigns`);
};

async function getCampaignPostsFromUrl(
  url: string,
): Promise<CampaignWithId[] | null> {
  const response = await fetch(url);
  if (!response.ok) {
    console.error("Error fetching campaignPosts", response.statusText);
    throw new Error("Network response was not ok");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const json: any[] = await response.json();
  return json.map((x) => {
    return {
      id: x.id,
      ...x.acf.header_campaign,
    };
  });
}

export const getPage = async (
  slug: string,
  lang?: string,
  defaultLang?: string,
) => {
  const pageRes = await fetchFromVolatileServer(
    getUrl(lang, defaultLang) + "/pages?slug=" + slug,
  );
  return await pageRes.json();
};

export const getPostForId = async ({
  id,
  postType,
  locale,
  defaultLocale,
  fields,
}: {
  id: number;
  postType: string;
  locale?: string;
  defaultLocale?: string;
  fields?: string;
}): Promise<Post | null> => {
  const postRes = await fetchFromVolatileServer(
    getUrl(locale, defaultLocale) +
      "/" +
      postType +
      "/" +
      id +
      `?acf_format=light${fields ? `&_fields=${fields}` : ""}`,
  );
  return await postRes.json();
};
